import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, GraduationCap, Users, Megaphone, CalendarDays, CheckCircle2, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const offerings = [
  // Archived (Sept 2026): Play Science Institute offering removed per user request.
  // Removed (June 2026 session): DC Men in ECE conference section removed per user request.
  {
    id: 'naeyc-2026',
    number: '02',
    icon: GraduationCap,
    title: 'NAEYC 2026 Annual Conference',
    tagline: 'December 5–8, 2026 · Washington, DC',
    intro: "The nation's largest early childhood education conference is coming to our own backyard this December. Join tens of thousands of educators, directors, and advocates from across the country for four days of professional learning, research-based sessions, networking, and inspiration — right here in Washington, DC. Early registration rates won't last, so secure your spot today and represent DC's early learning community on the national stage.",
    href: 'https://www.naeyc.org/events/annual/registration',
    cta: 'Register for NAEYC 2026',
    external: true,
    accent: 'teal',
    panel: 'from-[#0A2037] via-[#0f3050] to-teal-900',
    hideNumber: true,
    image: 'https://static.prod-images.emergentagent.com/jobs/55671c95-2135-4572-a38e-4ba882651c43/images/4d019fc6e37ac54b0c01313d2adeeb197fcef463b0bf2d5fd638c484fb53b8b2.jpeg',
    imageContain: false
  }
];

const organizerTrainings = [
  {
    key: 'organizing-101',
    date: 'November 10',
    title: 'Organizing 101 and Relationship Building',
    description: 'Learn how community organizing brings people together to build power and create change. Participants will practice one-on-one conversations that help us learn more about one another and move people to action.'
  },
  {
    key: 'storytelling',
    date: 'November 12',
    title: 'Storytelling',
    description: 'Explore how to use personal stories to connect with others and encourage collective action. Participants will practice sharing stories that connect their experiences and values to the issue they care about.'
  },
  {
    key: 'power-mapping',
    date: 'November 19',
    title: 'Power Mapping & Research Meetings',
    description: "Learn how to identify who has power to meet a campaign's demands and use research meetings to inform campaign strategy. Participants will practice mapping power, analyzing self-interest, and roleplay a research meeting with an elected official."
  }
];

const HEARD_OPTIONS = ['DCELC newsletter', 'Social media', 'Word of mouth', 'Partner organization', 'Other'];

const OrganizerRegistrationForm = () => {
  const [form, setForm] = useState({ name: '', email: '', organization: '', role: '', heard_about: '' });
  const [attendance, setAttendance] = useState('in-person');
  const [sessions, setSessions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const toggleSession = (key) => setSessions(v => v.includes(key) ? v.filter(x => x !== key) : [...v, key]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) { setError('Please enter your name and email.'); return; }
    if (sessions.length === 0) { setError('Please select at least one training date.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizer-training/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, attendance, sessions })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Registration failed — please try again.');
      setDone(d.sessions);
    } catch (err) { setError(err.message); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="organizer-register-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" aria-hidden="true" />
        <h4 className="text-lg font-bold text-slate-800 mb-1">You're registered!</h4>
        <p className="text-sm text-slate-600">
          We've emailed you a confirmation for {done.length} session{done.length !== 1 ? 's' : ''}, and you'll get a reminder the day before each one.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="organizer-register-form">
      <h4 className="text-lg font-bold text-slate-800 mb-1">Register for the Series</h4>
      <p className="text-sm text-slate-500 mb-5">Select which training{organizerTrainings.length > 1 ? 's' : ''} you'd like to register for — registration is per session. We'll email a confirmation and a reminder the day before each session you choose. A participation certificate will be issued for each training you complete.</p>
      <fieldset className="mb-5">
        <legend className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Which trainings would you like to register for?</legend>
        <div className="flex flex-wrap gap-2">
          {organizerTrainings.map(t => (
            <label key={t.key} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${sessions.includes(t.key) ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
              <input type="checkbox" className="h-4 w-4" checked={sessions.includes(t.key)} onChange={() => toggleSession(t.key)} data-testid={`organizer-session-${t.key}`} />
              <span><strong>{t.date}</strong> · {t.title}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="mb-5">
        <legend className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">How will you attend?</legend>
        <div className="flex flex-wrap gap-2">
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${attendance === 'in-person' ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
            <input type="radio" name="attendance" className="h-4 w-4" checked={attendance === 'in-person'} onChange={() => setAttendance('in-person')} data-testid="organizer-attendance-in-person" />
            <span><strong>In person</strong> · strongly recommended</span>
          </label>
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${attendance === 'virtual' ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
            <input type="radio" name="attendance" className="h-4 w-4" checked={attendance === 'virtual'} onChange={() => setAttendance('virtual')} data-testid="organizer-attendance-virtual" />
            <span>Virtual</span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">Attending in person is strongly recommended to get the most out of the training.</p>
      </fieldset>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <label htmlFor="org-reg-name" className="block text-xs font-medium text-slate-600 mb-1">Full Name *</label>
          <input id="org-reg-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="organizer-name-input" />
        </div>
        <div>
          <label htmlFor="org-reg-email" className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
          <input id="org-reg-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="organizer-email-input" />
        </div>
        <div>
          <label htmlFor="org-reg-org" className="block text-xs font-medium text-slate-600 mb-1">Organization</label>
          <input id="org-reg-org" value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="organizer-org-input" />
        </div>
        <div>
          <label htmlFor="org-reg-role" className="block text-xs font-medium text-slate-600 mb-1">Role</label>
          <input id="org-reg-role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g., Director, Lead Teacher, Advocate"
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="organizer-role-input" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="org-reg-heard" className="block text-xs font-medium text-slate-600 mb-1">How did you hear about the series?</label>
          <select id="org-reg-heard" value={form.heard_about} onChange={e => setForm(f => ({ ...f, heard_about: e.target.value }))}
            className="w-full sm:w-1/2 border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400" data-testid="organizer-heard-select">
            <option value="">Select one (optional)</option>
            {HEARD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="text-sm text-rose-600 font-medium mb-4" role="alert" data-testid="organizer-register-error">{error}</p>}
      <button type="submit" disabled={submitting || sessions.length === 0} data-testid="organizer-register-submit"
        className="inline-flex items-center gap-2 px-7 py-3 bg-rose-700 hover:bg-rose-800 text-white font-semibold rounded-full transition-colors duration-300 disabled:opacity-50">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <CalendarDays className="w-4 h-4" aria-hidden="true" />}
        {sessions.length === 0 ? 'Select at least one training' : `Register for ${sessions.length} Session${sessions.length !== 1 ? 's' : ''}`}
      </button>
    </form>
  );
};

const trainerBio = [
  'Aaron Booe is the Community Organizer at Rising Organizers. He brings years of experience working with youth and leading grassroots campaigns for racial and economic justice. Aaron began organizing as a high school student in Texas, where they led campaigns against school segregation and advocated for equitable public education.',
  'While attending Howard University, they organized with SEIU Local 500 to support the non-tenured faculty union, helping to build a strong student-labor coalition on campus. Previously, Aaron worked with Black Swan Academy, where they supported middle and high school students in campaigns focused on public safety and education in D.C.',
  "At Rising Organizers, Aaron helps RO alumni stay connected, strengthen the skills of emerging activists, and builds relationships with partner organizations. They are currently working on campaigns to defend D.C.'s home rule and fight for a people-centered city budget that reflects the needs of DC's working-class communities."
];

const accentStyles = {
  amber: { badge: 'bg-amber-100 text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700', num: 'text-amber-200' },
  indigo: { badge: 'bg-indigo-100 text-indigo-700', btn: 'bg-indigo-600 hover:bg-indigo-700', num: 'text-indigo-200' },
  teal: { badge: 'bg-teal-100 text-teal-700', btn: 'bg-teal-600 hover:bg-teal-700', num: 'text-teal-200' }
};

const ECEProfessionalDevelopment = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero */}
      <section id="main-content" role="main" tabIndex={-1} className="pt-32 pb-16 bg-gradient-to-br from-[#0A2037] via-slate-900 to-amber-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-20 w-40 h-40 border border-white/10 rounded-full" />
          <div className="absolute bottom-10 right-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 mb-8 transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-300 rounded-full text-sm font-semibold mb-6">
            <GraduationCap className="w-4 h-4" />
            Grow With Us
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="ece-pd-title">
            ECE Professional Development
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl leading-relaxed">
            DCELC invests in the people who power early learning. Our professional development offerings bring directors, lead teachers, and educators together for research-grounded training, leadership growth, and community — strengthening the ECE workforce across the District. Explore what's coming up below.
          </p>
        </div>
      </section>

      {/* Organizer Training Series */}
      <section aria-labelledby="organizer-training-title" data-testid="organizer-training-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden">
          <div className="bg-gradient-to-br from-rose-900 via-[#0A2037] to-slate-900 px-8 sm:px-12 py-10">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-rose-500/20 text-rose-200 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Megaphone className="w-3.5 h-3.5" aria-hidden="true" />
              New · Fall Training Series
            </span>
            <h2 id="organizer-training-title" className="text-2xl sm:text-3xl font-bold text-white mb-2">Organizer Training Series</h2>
            <p className="text-slate-300 max-w-3xl leading-relaxed">
              A three-part series with Rising Organizers that equips early learning advocates with practical community organizing skills — from building relationships to mapping power.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-slate-200" data-testid="organizer-training-logistics">
              <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-rose-300" aria-hidden="true" /> 6:00 PM – 8:00 PM · snacks provided</span>
              <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-rose-300" aria-hidden="true" /> 700 Pennsylvania Avenue, 2nd Floor, Washington, DC 20003</span>
              <span className="inline-flex items-center gap-2"><GraduationCap className="w-4 h-4 text-rose-300" aria-hidden="true" /> Participation certificates issued for each training completed</span>
            </div>
          </div>

          <div className="p-8 sm:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {organizerTrainings.map((t) => (
                <article key={t.date} data-testid={`organizer-training-${t.date.replace(/\s/g, '-').toLowerCase()}`} className="border border-slate-200 rounded-2xl p-6 hover:border-rose-300 hover:shadow-md transition-all duration-300 flex flex-col">
                  <span className="inline-flex items-center gap-2 self-start px-3 py-1 bg-rose-100 text-rose-800 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                    <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
                    {t.date}
                  </span>
                  <h3 className="text-lg font-bold text-slate-800 mb-3">{t.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.description}</p>
                </article>
              ))}
            </div>

            {/* Registration */}
            <div className="mb-12">
              <OrganizerRegistrationForm />
            </div>

            {/* Trainer bio */}
            <div className="border-t border-slate-100 pt-10" data-testid="organizer-trainer-bio">
              <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8 items-start">
                <figure className="mx-auto md:mx-0">
                  <img
                    src="https://customer-assets-cm19k8pv.emergentagent.net/job_55671c95-2135-4572-a38e-4ba882651c43/artifacts/2562d3a9168a390f_image.png"
                    alt="Aaron Booe, Community Organizer at Rising Organizers"
                    className="w-56 h-64 object-cover object-top rounded-2xl shadow-md"
                  />
                  <figcaption className="mt-3 text-center md:text-left">
                    <p className="font-bold text-slate-800">Aaron Booe</p>
                    <p className="text-xs text-slate-500">Community Organizer, Rising Organizers</p>
                  </figcaption>
                </figure>
                <div>
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-bold uppercase tracking-wider mb-4">Meet the Trainer</span>
                  {trainerBio.map((p, i) => (
                    <p key={i} className="text-slate-600 leading-relaxed mb-4 last:mb-0">{p}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offering Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-16">
        {offerings.map((o, index) => {
          const s = accentStyles[o.accent];
          const Icon = o.icon;
          const reversed = index % 2 === 1;
          return (
            <section
              key={o.id}
              aria-labelledby={`offering-${o.id}-title`}
              data-testid={`offering-section-${o.id}`}
              className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden"
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 ${reversed ? 'lg:[direction:rtl]' : ''}`}>
                {/* Visual panel */}
                <div className={`relative min-h-[280px] lg:min-h-[360px] bg-gradient-to-br ${o.panel} [direction:ltr]`}>
                  <img
                    src={o.image}
                    alt={o.title}
                    className={`absolute inset-0 w-full h-full ${o.imageContain ? 'object-contain p-4' : 'object-cover'}`}
                  />
                  {!o.hideNumber && (
                    <span className={`absolute top-6 left-6 text-6xl font-black ${s.num} opacity-90 drop-shadow-lg`} aria-hidden="true" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {o.number}
                    </span>
                  )}
                </div>

                {/* Text */}
                <div className="p-8 sm:p-12 flex flex-col justify-center [direction:ltr]">
                  <span className={`inline-flex items-center gap-2 self-start px-3 py-1 ${s.badge} rounded-full text-xs font-bold uppercase tracking-wider mb-4`}>
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    Professional Development
                  </span>
                  <h2 id={`offering-${o.id}-title`} className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                    {o.title}
                  </h2>
                  <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{o.tagline}</p>
                  <p className="text-slate-600 leading-relaxed mb-8">{o.intro}</p>
                  <div>
                    {o.external ? (
                      <a
                        href={o.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`offering-cta-${o.id}`}
                        aria-label={`${o.cta} (opens in a new tab)`}
                        className={`inline-flex items-center gap-2 px-6 py-3 ${s.btn} text-white font-semibold rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500`}
                      >
                        {o.cta}
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      </a>
                    ) : (
                      <Link
                        to={o.href}
                        data-testid={`offering-cta-${o.id}`}
                        className={`inline-flex items-center gap-2 px-6 py-3 ${s.btn} text-white font-semibold rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500`}
                      >
                        {o.cta}
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A2037] to-amber-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay in the Loop</h2>
          <p className="text-slate-300 mb-8 text-lg">
            New trainings and institutes are added throughout the year. Subscribe to our newsletter to hear about them first.
          </p>
          <Link
            to="/newsletter"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-[#0A2037] font-bold rounded-full transition-colors duration-300"
            data-testid="ece-pd-newsletter-cta"
          >
            Subscribe to the Newsletter
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ECEProfessionalDevelopment;
